import { useState } from 'react';
import {
  FileText, Filter, RefreshCw, DollarSign, Clock, CheckCircle, Download 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InvoiceCard } from '@/features/invoices';
import {
  useInvoices, useSubmitInvoice 
} from '@/hooks/use-invoices';
import {
  type InvoiceStatus, INVOICE_STATUS_LABELS 
} from '@/types/invoices';
import { toast } from 'sonner';

const STATUS_FILTERS: (InvoiceStatus | 'all')[] = [
  'all',
  'draft',
  'submitted',
  'pending',
  'approved',
  'paid',
];

export function InvoicesPage() {
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const {
    data, isLoading, error, refetch 
  } = useInvoices(
    statusFilter === 'all' ? undefined : { status: statusFilter }
  );
  const submitInvoice = useSubmitInvoice();

  const handleSubmit = (invoiceId: string) => {
    submitInvoice.mutate(invoiceId);
  };

  const handleViewDetails = (invoiceId: string) => {
    // Invoice details page navigation will be implemented when the route is created
    console.log('View invoice:', invoiceId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="grid gap-4 md:grid-cols-3 mb-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-24 animate-pulse bg-muted" />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-64 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">Failed to load invoices</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invoices = data?.data ?? [];
  const meta = data?.meta;

  const handleExport = () => {
    if (invoices.length === 0) {
      toast.info('No data to export');
      return;
    }
    const exportData = invoices.map((inv) => ({
      invoiceNumber: inv.invoiceNumber,
      poNumber: inv.poNumber ?? '',
      status: inv.status,
      total: inv.total,
      currency: inv.currency,
      dueDate: inv.dueDate,
      invoiceDate: inv.invoiceDate,
      paymentDate: inv.paymentDate ?? '',
    }));
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map((row) =>
        headers.map((h) => {
          const val = row[h as keyof typeof row];
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replaceAll('"', '""')}"`;
          }
          return String(val ?? '');
        }).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invoices.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${invoices.length} invoices`);
  };

  return (
    <div className="space-y-6">
      <PageHeader />

      {meta && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-blue-100 p-3">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Invoiced</p>
                <p className="text-xl font-bold">${meta.totalAmount.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-amber-100 p-3">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Payment</p>
                <p className="text-xl font-bold">${meta.pendingAmount.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Paid</p>
                <p className="text-xl font-bold">${meta.paidAmount.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {STATUS_FILTERS.map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
          >
            {status === 'all' ? 'All Invoices' : INVOICE_STATUS_LABELS[status]}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {invoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No invoices</p>
            <p className="text-muted-foreground">
              {statusFilter === 'all'
                ? 'You have no invoices yet'
                : `No invoices with status "${INVOICE_STATUS_LABELS[statusFilter]}"`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {invoices.map((invoice) => (
            <InvoiceCard
              key={invoice.invoiceId}
              invoice={invoice}
              onSubmit={handleSubmit}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold">Invoices</h1>
      <p className="text-muted-foreground">
        Manage and track your invoices and payments
      </p>
    </div>
  );
}
